import { useCallback, useState } from "react";

// #3186 -- "is the data for THIS key here yet?", derived at render time.
//
// The old shape was `const [loading, setLoading] = useState(false)` plus a
// `setLoading(true)` inside the fetch effect. React paints once BEFORE that
// effect runs, so on the first draw after a key appears (mount, a show or
// class switch, a dialog opening) `loading` was false and the list was still
// empty: the "no data" text flashed for a frame while the data was in flight.
//
// Here `loading` is not a flag the effect flips, it is a comparison:
// "there is a key, and nothing has been marked loaded for that exact key".
// It is true on the very first render with the key and stays true until the
// fetch calls `markLoaded(key)` (success OR failure, so an error never leaves
// a skeleton up forever). `key` is whatever identifies the thing being
// loaded (a show id, a class id, a composite string); a falsy key means
// nothing to load, so `loading` is false.
//
// Call `markLoaded(null)` at the top of the effect so returning to a key whose
// data the effect just cleared (A -> B -> A before B settles) reads as loading
// again instead of as a settled-empty list.
export function useLoadedFor(key) {
  const [loadedFor, setLoadedFor] = useState(null);
  const markLoaded = useCallback((forKey) => setLoadedFor(forKey), []);
  return [Boolean(key) && loadedFor !== key, markLoaded];
}

export default useLoadedFor;
